import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import {
    PositionModel,
    PositionType,
    Side,
    OptionType,
    BudgetModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    LocationModel,
} from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { getToolDescription } from './Types';

const MMBTU_PER_CONTRACT = 10000;

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

interface WeightedCostAvgDisplayRow {
    tenureKey: string;
    formattedDate: string;
    counterpartyName: string;
    tradeDate: string;
    contracts: number;
    tool: string;
    strike: number;
    purchasePrice: number;
    // Group totals (only populated on the first row of each tenure group)
    totalContractsPurchased: number | null;
    maxContracts: number | null;
    weightedStrikeAverage: number | null;
    weightedPurchasePriceAverage: number | null;
    isGroupStart: boolean;
    groupSize: number;
    position: PositionModel;
}

interface WeightedCostAvgReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
}

const WeightedCostAvgReport: React.FC<WeightedCostAvgReportProps> = ({
    customerData,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [budgets, setBudgets] = useState<BudgetModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());
    const [hasRun, setHasRun] = useState<boolean>(false);

    // Default start = first of next month, end = one year from today (match HedgingMTM)
    const [startMonth, setStartMonth] = useState<number>(dayjs().add(1, 'month').month() + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().add(1, 'month').year());
    const [endMonth, setEndMonth] = useState<number>(dayjs().add(1, 'year').month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().add(1, 'year').year());

    const locations: LocationModel[] = customerData?.locations || [];
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (selectedLocation === undefined && locations.length > 0 && locations[0].locationId !== undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    const fetchData = async () => {
        const posResponse = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            false,
            true,
            true,
            undefined,
            undefined,
            selectedLocation ? [selectedLocation] : undefined,
            undefined,
            undefined,
            undefined,
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        const budResponse = await CommodityManagementApi.listBudgets(
            null,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            startYear,
            startMonth,
            undefined,
            endYear,
            endMonth,
            undefined,
            undefined,
            undefined,
            selectedLocation ? [selectedLocation] : undefined,
            undefined,
            undefined,
            undefined,
            false,
            false,
            false
        );

        setPositions(posResponse.data.rows || []);
        setBudgets(budResponse.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchData();
    };

    // Only bought call options are included (matches piedmont stored proc)
    const callPositions = useMemo(() => {
        return positions.filter(p =>
            !p.isExpired &&
            p.positionType === PositionType.Option &&
            p.optionType === OptionType.Call &&
            p.side === Side.Buy
        );
    }, [positions]);

    // Budget volume per month (MMBtus) used for "Maximum # of Contracts"
    const budgetVolumeByMonth = useMemo(() => {
        const map = new Map<string, number>();
        budgets.forEach(b => {
            if (b.budgetDate?.year !== undefined && b.budgetDate?.month !== undefined) {
                const key = `${b.budgetDate.year}-${String(b.budgetDate.month).padStart(2, '0')}`;
                map.set(key, (map.get(key) ?? 0) + (b.volume ?? 0));
            }
        });
        return map;
    }, [budgets]);

    const commodityName = useMemo(() => {
        return callPositions[0]?.commodity?.commodityName || '';
    }, [callPositions]);

    const reportRows: WeightedCostAvgDisplayRow[] = useMemo(() => {
        // Group by tenure (budget year-month)
        const groups = new Map<string, PositionModel[]>();
        callPositions.forEach(p => {
            const y = p.budgetDate?.year ?? 0;
            const m = p.budgetDate?.month ?? 1;
            const key = `${y}-${String(m).padStart(2, '0')}`;
            const list = groups.get(key) ?? [];
            list.push(p);
            groups.set(key, list);
        });

        const sortedKeys = Array.from(groups.keys()).sort();

        const rows: WeightedCostAvgDisplayRow[] = [];
        sortedKeys.forEach(key => {
            const group = (groups.get(key) ?? []).slice().sort((a, b) => {
                const aDate = a.tradeDate ? new Date(a.tradeDate).getTime() : 0;
                const bDate = b.tradeDate ? new Date(b.tradeDate).getTime() : 0;
                return aDate - bDate;
            });

            const totalVolume = group.reduce((s, p) => s + (p.volume ?? 0), 0);
            const totalContracts = totalVolume / MMBTU_PER_CONTRACT;
            const weightedStrike = totalVolume > 0
                ? group.reduce((s, p) => s + (p.optionStrike ?? 0) * (p.volume ?? 0), 0) / totalVolume
                : 0;
            const weightedPrice = totalVolume > 0
                ? group.reduce((s, p) => s + (p.price ?? 0) * (p.volume ?? 0), 0) / totalVolume
                : 0;
            const maxContracts = (budgetVolumeByMonth.get(key) ?? 0) / MMBTU_PER_CONTRACT;

            const [yStr, mStr] = key.split('-');
            const formattedDate = dayjs(`${yStr}-${mStr}-01`).format('MMM-YYYY');

            group.forEach((position, idx) => {
                const isGroupStart = idx === 0;
                rows.push({
                    tenureKey: key,
                    formattedDate,
                    counterpartyName: position.counterparty?.counterpartyName || '',
                    tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                    contracts: (position.volume ?? 0) / MMBTU_PER_CONTRACT,
                    tool: getToolDescription(position),
                    strike: position.optionStrike ?? 0,
                    purchasePrice: position.price ?? 0,
                    totalContractsPurchased: isGroupStart ? totalContracts : null,
                    maxContracts: isGroupStart ? maxContracts : null,
                    weightedStrikeAverage: isGroupStart ? weightedStrike : null,
                    weightedPurchasePriceAverage: isGroupStart ? weightedPrice : null,
                    isGroupStart,
                    groupSize: group.length,
                    position,
                });
            });
        });

        return rows;
    }, [callPositions, budgetVolumeByMonth]);

    const fmtContracts = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '';
    const fmtStrike = (v: number | null | undefined) =>
        v != null ? '$' + v.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '';
    const fmtPrice4 = (v: number | null | undefined) =>
        v != null ? '$' + v.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '';

    // A spanned cell sits on top of subsequent rows' cells. To cover the row
    // borders that would otherwise show through, render the value inside an
    // absolutely positioned element that fills the entire spanned cell and
    // draws its own opaque background + bottom border.
    const spannedCellStyle = {
        padding: 0,
        overflow: 'visible' as const,
    };

    const makeSpannedRenderer = (formatter: (v: number | null | undefined) => string) =>
        (p: { value: number | null | undefined; data?: WeightedCostAvgDisplayRow }) => {
            if (!p.data?.isGroupStart) return null;
            const text = formatter(p.value);
            return (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        background: 'var(--ag-background-color, #ffffff)',
                        borderBottom: '1px solid var(--ag-border-color, #babfc7)',
                        zIndex: 2,
                    }}
                >
                    {text}
                </div>
            );
        };

    const columnDefs: ColDef<WeightedCostAvgDisplayRow>[] = useMemo(() => [
        { field: 'formattedDate', headerName: 'Period', flex: 1, minWidth: 100, pinned: 'left' },
        { field: 'counterpartyName', headerName: 'Counterparty', flex: 1.3, minWidth: 140 },
        { field: 'tradeDate', headerName: 'Trade Date', flex: 1, minWidth: 110 },
        {
            field: 'contracts',
            headerName: 'Contracts',
            flex: 1,
            minWidth: 100,
            type: 'numericColumn',
            valueFormatter: p => fmtContracts(p.value),
        },
        { field: 'tool', headerName: 'Tool', flex: 1.2, minWidth: 130 },
        {
            field: 'strike',
            headerName: 'Strike',
            flex: 1,
            minWidth: 110,
            type: 'numericColumn',
            valueFormatter: p => fmtStrike(p.value),
        },
        {
            field: 'purchasePrice',
            headerName: 'Purchase Price',
            flex: 1.1,
            minWidth: 130,
            type: 'numericColumn',
            valueFormatter: p => fmtPrice4(p.value),
        },
        {
            field: 'totalContractsPurchased',
            headerName: 'Total Contracts Purchased',
            flex: 1.4,
            minWidth: 160,
            rowSpan: p => p.data?.isGroupStart ? (p.data.groupSize || 1) : 1,
            cellStyle: spannedCellStyle,
            cellRenderer: makeSpannedRenderer(fmtContracts),
        },
        {
            field: 'maxContracts',
            headerName: 'Maximum # of Contracts',
            flex: 1.4,
            minWidth: 160,
            rowSpan: p => p.data?.isGroupStart ? (p.data.groupSize || 1) : 1,
            cellStyle: spannedCellStyle,
            cellRenderer: makeSpannedRenderer(fmtContracts),
        },
        {
            field: 'weightedStrikeAverage',
            headerName: 'Weighted Strike Average',
            flex: 1.4,
            minWidth: 160,
            rowSpan: p => p.data?.isGroupStart ? (p.data.groupSize || 1) : 1,
            cellStyle: spannedCellStyle,
            cellRenderer: makeSpannedRenderer(fmtPrice4),
        },
        {
            field: 'weightedPurchasePriceAverage',
            headerName: 'Weighted Purchase Price Average',
            flex: 1.5,
            minWidth: 180,
            rowSpan: p => p.data?.isGroupStart ? (p.data.groupSize || 1) : 1,
            cellStyle: spannedCellStyle,
            cellRenderer: makeSpannedRenderer(fmtPrice4),
        },
    ], []);

    const handleDownloadExcel = () => {
        if (reportRows.length === 0) return;

        const title = `Weight Cost Average - ${commodityName} Open Position`;
        const subtitle = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;
        const header = [
            'Period', 'Counterparty', 'Trade Date', 'Contracts', 'Tool', 'Strike', 'Purchase Price',
            'Total Contracts Purchased', 'Maximum # of Contracts',
            'Weighted Strike Average', 'Weighted Purchase Price Average',
        ];

        const aoa: (string | number)[][] = [];
        aoa.push([title]);
        aoa.push([subtitle]);
        aoa.push(header);

        reportRows.forEach(r => {
            aoa.push([
                r.formattedDate,
                r.counterpartyName,
                r.tradeDate,
                r.contracts,
                r.tool,
                r.strike,
                r.purchasePrice,
                r.isGroupStart ? (r.totalContractsPurchased ?? 0) : '',
                r.isGroupStart ? (r.maxContracts ?? 0) : '',
                r.isGroupStart ? (r.weightedStrikeAverage ?? 0) : '',
                r.isGroupStart ? (r.weightedPurchasePriceAverage ?? 0) : '',
            ]);
        });

        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        const lastCol = header.length - 1;
        const merges: XLSX.Range[] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        ];

        // Merge group-total columns vertically across each tenure group (match piedmont rowspan)
        const dataStart = 3;
        let groupStartRow = dataStart;
        for (let i = 0; i < reportRows.length; i++) {
            const row = reportRows[i];
            if (row.isGroupStart) groupStartRow = dataStart + i;
            const next = reportRows[i + 1];
            const isGroupEnd = !next || next.isGroupStart;
            if (isGroupEnd && row.groupSize > 1) {
                const endRow = dataStart + i;
                [7, 8, 9, 10].forEach(col => {
                    merges.push({ s: { r: groupStartRow, c: col }, e: { r: endRow, c: col } });
                });
            }
        }
        sheet['!merges'] = merges;

        const currency3 = '"$"#,##0.000;[Red]("$"#,##0.000)';
        const currency4 = '"$"#,##0.0000;[Red]("$"#,##0.0000)';
        const intFmt = '#,##0;(#,##0)';

        for (let i = 0; i < reportRows.length; i++) {
            const r = dataStart + i;
            const apply = (c: number, fmt: string) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[ref];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = fmt;
                }
            };
            apply(3, intFmt);
            apply(5, currency3);
            apply(6, currency4);
            apply(7, intFmt);
            apply(8, intFmt);
            apply(9, currency4);
            apply(10, currency4);
        }

        sheet['!cols'] = [
            { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 10 },
            { wch: 16 }, { wch: 12 }, { wch: 14 },
            { wch: 22 }, { wch: 20 }, { wch: 22 }, { wch: 26 },
        ];

        const workbook = XLSX.utils.book_new();
        const sheetName = (selectedLocationName || 'WtCostAvg').replace(/[\\/*?:[\]]/g, '').substring(0, 31) || 'WtCostAvg';
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

        const filename = `WeightedCostAverage_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Weighted Cost Average Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="Marking to Market Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(Number(e.target.value))}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Save to Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    {commodityName && (
                        <Typography variant="h6" sx={{ mt: 1 }}>
                            {`Weight Cost Average - ${commodityName} Open Position`}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {`Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`}
                    </Typography>
                    <StoneXGrid
                        columns={columnDefs}
                        rows={reportRows}
                        gridOptions={{ suppressRowTransform: true }}
                    />
                </>
            ) : null}
        </Box>
    );
};

export default WeightedCostAvgReport;
